import type { QueryClient } from "@tanstack/react-query";
import {
  GetEmployeesResponse,
  SenderId,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { unknownEmployee } from "tng-address-collection-shared/unknownEmployee.ts";
import { queryKeys } from "./queryKeys.ts";

export type SenderSignature = { uidNumber: number; name: string };

export const resolveEmployeeName = (
  queryClient: QueryClient,
  uidNumber: number,
): string => {
  const employees = queryClient.getQueryData<GetEmployeesResponse["employees"]>(
    queryKeys.employees(),
  );
  const match = employees?.find((e) => e.uidNumber === uidNumber);
  return (match ?? unknownEmployee(uidNumber)).name;
};

const isEmployeeSender = (
  senderId: SenderId | undefined,
): senderId is Extract<SenderId, { type: "employee" }> =>
  senderId?.type === "employee";

export const getSenderSignature = (
  queryClient: QueryClient,
  senderId: SenderId | undefined,
): SenderSignature | undefined => {
  if (!isEmployeeSender(senderId)) return undefined;
  return {
    uidNumber: senderId.uidNumber,
    name: resolveEmployeeName(queryClient, senderId.uidNumber),
  };
};

export const getSenderDisplayName = (
  queryClient: QueryClient,
  senderId: SenderId | undefined,
): string | undefined => {
  if (!senderId) return undefined;
  return senderId.type === "employee"
    ? resolveEmployeeName(queryClient, senderId.uidNumber)
    : senderId.email;
};

const isSignedByUser = (
  signatures: SenderSignature[],
  userUidNumber: number | undefined,
): boolean =>
  userUidNumber !== undefined &&
  signatures.some((s) => s.uidNumber === userUidNumber);

export const withSenderSignature = (
  existing: SenderSignature[],
  senderSignature: SenderSignature | undefined,
): SenderSignature[] => {
  if (!senderSignature) return existing;
  if (existing.some((s) => s.uidNumber === senderSignature.uidNumber)) {
    return existing;
  }
  return [...existing, senderSignature];
};

export const optimisticSignatureFields = (
  signatures: SenderSignature[],
  hiddenFields: string[] | undefined,
  userUidNumber: number | undefined,
): {
  signatures: SenderSignature[] | undefined;
  numberOfSignatures: number | undefined;
  isSignedByUser: boolean;
} => {
  const signaturesHidden =
    hiddenFields?.includes("signatures") ||
    hiddenFields?.includes("numberOfSignatures");
  return {
    signatures: signaturesHidden ? undefined : signatures,
    numberOfSignatures: signaturesHidden ? undefined : signatures.length,
    isSignedByUser: isSignedByUser(signatures, userUidNumber),
  };
};
